/**
 * ItemChat Component
 *
 * Per-item AI chat that persists with the item in Google Drive.
 * Displays chat history and allows back-and-forth conversation
 * about the specific item (link, podcast, note, idea, file, task).
 */

import { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, Trash2, Loader2, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { openRouter } from '@/services/openRouter';
import { useApp } from '@/context/AppContext';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/utils/cn';
import { nanoid } from 'nanoid';
import { usePopupChat } from '@/hooks/usePopupChat';
import {
  onChatSyncMessage,
  respondToSyncRequest,
  type ChatSyncMessage,
} from '@/services/chatSyncChannel';
import type { Item, AIMessage } from '@/types';

// ============================================
// TYPES
// ============================================

interface ItemChatProps {
  item: Item;
  onUpdateItem: (item: Item) => void;
}

// ============================================
// ITEM CHAT COMPONENT
// ============================================

export function ItemChat({ item, onUpdateItem }: ItemChatProps) {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Local state for immediate UI updates (avoids context sync issues)
  const [localHistory, setLocalHistory] = useState<AIMessage[]>(item.aiChatHistory ?? []);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { openModal } = useApp();
  const { error: showError } = useToast();

  const isConfigured = openRouter.isConfigured();

  // Popup chat management
  const { openPopup } = usePopupChat({ item });

  // Sync local state when item changes (e.g., modal opens with different item)
  useEffect(() => {
    setLocalHistory(item.aiChatHistory ?? []);
  }, [item.id, item.aiChatHistory]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localHistory.length, isLoading]);

  // Listen for sync messages from popup windows
  useEffect(() => {
    const unsubscribe = onChatSyncMessage((message: ChatSyncMessage) => {
      if (message.itemId !== item.id) return;

      switch (message.type) {
        case 'request-sync':
          // Popup requesting sync - respond with current state
          respondToSyncRequest(item.id, item, localHistory);
          break;

        case 'new-message':
          // Popup sent a message - update local state and persist
          if (message.history) {
            setLocalHistory(message.history);
            onUpdateItem({ ...item, aiChatHistory: message.history });
          }
          break;

        case 'clear-chat':
          // Popup cleared chat
          setLocalHistory([]);
          onUpdateItem({ ...item, aiChatHistory: [] });
          break;
      }
    });

    return unsubscribe;
  }, [item, onUpdateItem, localHistory]);

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;

    const userMsg: AIMessage = {
      id: nanoid(8),
      role: 'user',
      content: message.trim(),
      timestamp: new Date().toISOString(),
    };

    // Immediate local update for responsive UI
    const historyWithUser = [...localHistory, userMsg];
    setLocalHistory(historyWithUser);
    setMessage('');
    setIsLoading(true);

    try {
      const response = await openRouter.chatAboutItem(item, userMsg.content, localHistory);

      const assistantMsg: AIMessage = {
        id: nanoid(8),
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString(),
      };

      // Update local state immediately
      const newHistory = [...historyWithUser, assistantMsg];
      setLocalHistory(newHistory);

      // Persist to backend (async, non-blocking for UI)
      onUpdateItem({ ...item, aiChatHistory: newHistory });
    } catch (err) {
      showError('AI Error', err instanceof Error ? err.message : 'Failed to get response');
      // Revert local state on error
      setLocalHistory(item.aiChatHistory ?? []);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleClearChat = () => {
    if (window.confirm('Clear all chat history for this item?')) {
      setLocalHistory([]);
      onUpdateItem({ ...item, aiChatHistory: [] });
    }
  };

  const handleExpandClick = () => {
    const success = openPopup();
    if (!success) {
      showError(
        'Popup Blocked',
        'Please allow popups for this site to use the expanded chat view.'
      );
    }
  };

  // Disabled state - prompt to configure API key
  if (!isConfigured) {
    return (
      <div className="border-t pt-4 mt-4">
        <button
          type="button"
          onClick={() => openModal({ mode: 'settings' })}
          className="w-full text-center text-gray-500 py-4 hover:bg-gray-50 rounded-lg transition-colors"
        >
          <Sparkles className="h-5 w-5 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Click to configure API key and enable AI chat</p>
        </button>
      </div>
    );
  }

  return (
    <div className="border-t pt-4 mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            AI Chat
          </h4>
          <p className="text-xs text-gray-400 mt-0.5">AI uses last 10 messages for context</p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExpandClick}
            title="Open in popup window"
            className="text-gray-500"
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
          {localHistory.length > 0 && (
            <Button variant="ghost" size="sm" onClick={handleClearChat} className="text-gray-500">
              <Trash2 className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Chat messages */}
      {localHistory.length > 0 && (
        <div className="space-y-3 max-h-64 overflow-y-auto mb-3 pr-1">
          {localHistory.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                'text-sm p-3 rounded-lg',
                msg.role === 'user'
                  ? 'bg-brand-50 ml-8 text-gray-900 whitespace-pre-wrap'
                  : 'bg-gray-100 mr-8 text-gray-700'
              )}
            >
              {msg.role === 'assistant' ? (
                <div className="prose prose-sm max-w-none prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
              ) : (
                msg.content
              )}
            </div>
          ))}
          {isLoading && (
            <div className="bg-gray-100 mr-8 p-3 rounded-lg flex items-center gap-2 text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Thinking...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input */}
      <div className="flex gap-2">
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about this item..."
          disabled={isLoading}
          className="flex-1"
        />
        <Button
          onClick={() => void handleSend()}
          disabled={!message.trim() || isLoading}
          size="icon"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
